import type { StreamField } from '@/api/streamsApi'
import { getTimestampTooltip } from '@/lib/utils'
import { isDateOnlyType, isDateTimeType } from '@/utils/clickhouseTypes'
import convert from 'convert-units'

// Re-exported for the panel utils barrel and its consumers; defined in @/utils/clickhouseTypes.
export { isDateTimeType }

/**
 * Coerce a panel value to a finite number, or null if it isn't numeric.
 * Handles BigInt (json-bigint parses large ints as BigInt) and numeric strings —
 * ClickHouse may return 64-bit integers as quoted strings (e.g. count() → "42").
 */
export function toNumeric(v: unknown): number | null {
	if (typeof v === 'number') {
		return Number.isFinite(v) ? v : null
	}
	if (typeof v === 'bigint') {
		return Number(v)
	}
	if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) {
		return Number(v)
	}
	return null
}

export function formatNumberWithUnit(
	value: number,
	unit: string,
	enableRoundOff: boolean,
	roundOffPrecision = 3,
): string {
	const roundedValue = enableRoundOff
		? Number.parseFloat(value.toFixed(roundOffPrecision)).toString()
		: value.toString()
	return `${roundedValue} ${unit}`
}

// Convert number to hexadecimal format
export function formatNumberAsHex(value: number | bigint): string {
	return `0x${value.toString(16).toUpperCase()}`
}

// Round value based on precision, handling both number and BigInt
function roundValue(value: number | bigint, roundOffPrecision: number): number | bigint {
	if (typeof value === 'bigint') {
		return value // BigInt doesn't need rounding
	}
	return Number.parseFloat(value.toFixed(roundOffPrecision))
}

// convert-units uses binary (1024) for digital units and toBest() won't scale B→KB at 1000 bytes.
// Custom decimal (1000-based) scaling used instead to match SI convention (1,000,000 B = 1 MB).
const DIGITAL_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const
type DigitalUnit = (typeof DIGITAL_UNITS)[number]
// Normalized to uppercase for case-insensitive matching (e.g. 'kb' → 'KB')
const DIGITAL_UNIT_SET = new Set<string>(DIGITAL_UNITS)

// Temperature units — no meaningful "larger" unit progression
const TEMPERATURE_UNIT_SET = new Set(['C', 'F', 'K', 'R'])

// Units intentionally excluded: % is a ratio (scaling distorts meaning); m/s2, ohm, N have no intuitive larger scale (e.g. 100 ohm → 0.1 kohm is unfamiliar to users)
const NO_AUTO_SCALE_UNITS = new Set(['%', 'm/s2', 'ohm', 'N'])

function autoScaleBytes(value: number, inputUnit: DigitalUnit): { val: number; unit: string } {
	const unitIndex = DIGITAL_UNITS.indexOf(inputUnit)
	let bytes = value * 1000 ** unitIndex
	// Preserve sign — scaling loops require positive values, sign restored at end
	const sign = Math.sign(bytes)
	bytes = Math.abs(bytes)

	let outIndex = 0
	while (bytes >= 1000 && outIndex < DIGITAL_UNITS.length - 1) {
		bytes /= 1000
		outIndex++
	}
	while (bytes < 1 && outIndex > 0) {
		bytes *= 1000
		outIndex--
	}

	return { val: sign * bytes, unit: DIGITAL_UNITS[outIndex] }
}

function autoScaleUnit(value: number, unit: string): { val: number; unit: string } {
	const normalizedUnit = unit.toUpperCase()

	// toBest() has no candidate at or above 1 for 0, so it falls back to the smallest unit ("0 mm").
	if (value === 0 || NO_AUTO_SCALE_UNITS.has(normalizedUnit)) {
		return { val: value, unit }
	}

	if (DIGITAL_UNIT_SET.has(normalizedUnit)) {
		return autoScaleBytes(value, normalizedUnit as DigitalUnit)
	}

	if (TEMPERATURE_UNIT_SET.has(normalizedUnit)) {
		return { val: value, unit }
	}

	try {
		// Type assertion needed because convert-units accepts dynamic string units
		const converted = convert(value)
			.from(unit as unknown as Parameters<ReturnType<typeof convert>['from']>[0])
			.toBest()
		return { val: converted.val, unit: converted.unit }
	} catch {
		// If conversion fails (unit not supported), return original value
		return { val: value, unit }
	}
}

// Format a number with optional unit conversion and scaling
function formatNumber(
	value: number | bigint,
	valueUnit: string | null,
	asHex: boolean,
	enableRoundOff: boolean,
	enableUnits: boolean,
	autoScaleUnits: boolean,
	roundOffPrecision = 3,
): string {
	// Handle hex format first
	if (asHex) {
		return formatNumberAsHex(value)
	}

	// Skip rounding for BigInt values
	const roundedValue = enableRoundOff ? roundValue(value, roundOffPrecision) : value

	// Handle unit conversions
	if (valueUnit && enableUnits) {
		if (!autoScaleUnits) {
			return `${roundedValue.toString()} ${valueUnit}`
		}

		// Perform unit auto-scaling for numeric values
		// autoScaleUnit handles exclusions (NO_AUTO_SCALE_UNITS, TEMPERATURE_UNIT_SET) internally
		if (typeof roundedValue === 'number') {
			const valueObj = autoScaleUnit(roundedValue, valueUnit)
			return formatNumberWithUnit(valueObj.val, valueObj.unit, enableRoundOff, roundOffPrecision)
		}

		// BigInt: skip scaling — casting to number risks silent precision loss beyond MAX_SAFE_INTEGER
		return `${roundedValue.toString()} ${valueUnit}`
	}

	// If units are disabled or not applicable, return the value as string
	return typeof roundedValue === 'bigint' ? BigInt(roundedValue).toString() : roundedValue.toString()
}

export function formatValue(
	value: unknown,
	valueUnit: string | null = null,
	asHex = false,
	enableRoundOff = true,
	roundOffPrecision = 3,
	enableUnits = false,
	autoScaleUnits = false,
): string {
	if (typeof value === 'number' || typeof value === 'bigint') {
		return formatNumber(value, valueUnit, asHex, enableRoundOff, enableUnits, autoScaleUnits, roundOffPrecision)
	}

	if (typeof value === 'boolean') {
		return value ? 'true' : 'false'
	}

	return value != null ? String(value) : '-'
}

export function capitalizeFirstLetter(str: string): string {
	if (!str) {
		return ''
	}
	const stringVal = typeof str === 'string' ? str : String(str)
	return stringVal
		.split(/[^a-zA-Z0-9]+/)
		.filter(Boolean)
		.map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
		.join(' ')
}

// Format date/time value
export function formatDateTime(value: unknown): string {
	if (typeof value === 'string') {
		const date = new Date(value)
		if (!Number.isNaN(date.getTime())) {
			return getTimestampTooltip(value)
		}
	}

	if (typeof value === 'number') {
		// Assume milliseconds since epoch
		const date = new Date(value)
		if (!Number.isNaN(date.getTime())) {
			return getTimestampTooltip(value)
		}
	}

	return String(value)
}

const COMPACT_STEPS = [
	[1e9, 'B'],
	[1e6, 'M'],
	[1e3, 'k'],
] as const

/** A thousand and up as 1.2k, 3.45M or 5B. Smaller values keep two decimals, or two significant digits under one. */
export function formatCompactNumber(value: number): string {
	const abs = Math.abs(value)
	const step = COMPACT_STEPS.find(([size]) => abs >= size)
	if (step) {
		return `${Number((value / step[0]).toFixed(2))}${step[1]}`
	}
	return String(Number(abs < 1 ? value.toPrecision(2) : value.toFixed(2)))
}

interface FormatAggregateValueOptions {
	column: string
	prefix: string
	suffix: string
	aggregator: string
	value: unknown
	table: StreamField[]
	enableUnits: boolean
	autoScaleUnits: boolean
	enableRoundoff: boolean
	roundOffPrecision: number
}

// Format aggregate value with prefix, suffix, formatter, and datetime handling
export function formatAggregateValue(options: FormatAggregateValueOptions): string {
	const {
		column,
		prefix,
		suffix,
		aggregator,
		value,
		table,
		enableUnits,
		autoScaleUnits,
		enableRoundoff,
		roundOffPrecision,
	} = options

	const columnInfo = table?.find(col => col.name === column)
	let columnFormatter = columnInfo?.formatter ?? null

	// Count aggregators don't use formatter
	if (aggregator === 'count' || aggregator === 'count_distinct') {
		columnFormatter = null
		return `${prefix} ${value} ${suffix}`.trim()
	}

	// delta on date columns yields a numeric difference (days/seconds), not a timestamp —
	// skip date formatting and fall through to numeric formatting
	const isDelta = aggregator === 'delta'

	// Date-only columns (Date, Nullable(Date)) — return as-is, no timezone conversion
	if (!isDelta && columnInfo && isDateOnlyType(columnInfo.type)) {
		return `${prefix} ${value != null ? String(value) : '-'} ${suffix}`.trim()
	}

	// Handle DateTime types
	if (!isDelta && columnInfo && isDateTimeType(columnInfo.type)) {
		return `${prefix} ${formatDateTime(value)} ${suffix}`.trim()
	}

	// Format the value with formatter and rounding
	const text = formatValue(
		value,
		columnFormatter,
		false,
		enableRoundoff,
		roundOffPrecision,
		enableUnits,
		autoScaleUnits,
	)

	return withAffixes(text, prefix, suffix)
}

export const withAffixes = (text: string, prefix = '', suffix = ''): string => `${prefix} ${text} ${suffix}`.trim()

interface CreateAggregateValueFormatterConfig {
	table: StreamField[]
	enableUnits: boolean
	autoScaleUnits: boolean
	enableRoundoff: boolean
	roundOffPrecision: number
}

// Function to create a configured getText function for aggregate values
export function createAggregateValueFormatter(config: CreateAggregateValueFormatterConfig) {
	return (column: string, prefix: string, suffix: string, aggregator: string, value: unknown): string => {
		return formatAggregateValue({
			column,
			prefix,
			suffix,
			aggregator,
			value,
			table: config.table,
			enableUnits: config.enableUnits,
			autoScaleUnits: config.autoScaleUnits,
			enableRoundoff: config.enableRoundoff,
			roundOffPrecision: config.roundOffPrecision,
		})
	}
}
